/**
 * AdjustmentTableEditor — a single adjustment table (LLPA / eligibility / prepay
 * & sizing), one per ADJ_GROUPS panel. Ported from
 * digital_sizer/components/admin/AdjustmentTableEditor.tsx into eastviewos.
 *
 * The source's `ShapeKind` TS union becomes the SHAPE string constants below.
 * All five shapes are ported, each with its own editable table renderer:
 *   - fico_ltv_grid / dscr_ltv_grid / ppp_ltv_grid → row × column number grid
 *   - ltv_band_map → { bucketLabel: adjustment } two-column map
 *   - loan_amount_buckets → [{ label, min, max, adjustment }] rows
 * react-query is swapped for plain useState/useEffect via adminPricingApi; the
 * before/after review is a simple diff table (no diff lib). bulk-shift + lock +
 * history are wired through the same api module.
 */
import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import LockOutlined from "@mui/icons-material/LockOutlined";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import ChangeHistoryDrawer from "../../components/admin/ChangeHistoryDrawer";
import { adminPricingApi } from "../../api/adminPricingApi";
import { teal } from "../../theme/colors";

// ShapeKind (TS union → string constants)
export const SHAPE = {
  FICO_LTV_GRID: "fico_ltv_grid",
  DSCR_LTV_GRID: "dscr_ltv_grid",
  PPP_LTV_GRID: "ppp_ltv_grid",
  LTV_BAND_MAP: "ltv_band_map",
  LOAN_AMOUNT_BUCKETS: "loan_amount_buckets",
};

const isGridShape = (s) => s === SHAPE.FICO_LTV_GRID || s === SHAPE.DSCR_LTV_GRID || s === SHAPE.PPP_LTV_GRID;

const deepClone = (v) => JSON.parse(JSON.stringify(v));
const dataEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function fmtNum(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(4);
}

// ── Diff (modified cells) ────────────────────────────────────────────────────
function computeDiff(shape, before, after) {
  const out = [];
  const near = (bv, av) => typeof bv === "number" && typeof av === "number" && Math.abs(bv - av) < 1e-9;
  if (isGridShape(shape)) {
    const rowKeys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
    for (const r of rowKeys) {
      const bRow = (before || {})[r] || {};
      const aRow = (after || {})[r] || {};
      const colKeys = new Set([...Object.keys(bRow), ...Object.keys(aRow)]);
      for (const c of colKeys) {
        const bv = bRow[c] === undefined ? null : bRow[c];
        const av = aRow[c] === undefined ? null : aRow[c];
        if (bv !== av && !near(bv, av)) out.push({ rowLabel: r, colLabel: c, before: bv, after: av });
      }
    }
  } else if (shape === SHAPE.LTV_BAND_MAP) {
    const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
    for (const k of keys) {
      const bv = (before || {})[k];
      const av = (after || {})[k];
      if (bv !== av && !near(bv, av)) out.push({ rowLabel: k, before: bv ?? null, after: av ?? null });
    }
  } else if (shape === SHAPE.LOAN_AMOUNT_BUCKETS) {
    const beforeMap = new Map((before || []).map((b) => [b.label, b]));
    const afterMap = new Map((after || []).map((b) => [b.label, b]));
    const labels = new Set([...beforeMap.keys(), ...afterMap.keys()]);
    for (const l of labels) {
      const b = beforeMap.get(l);
      const a = afterMap.get(l);
      if (!b || !a) {
        out.push({ rowLabel: l, before: b?.adjustment ?? null, after: a?.adjustment ?? null });
      } else if (Math.abs(b.adjustment - a.adjustment) > 1e-9 || b.min !== a.min || b.max !== a.max) {
        out.push({ rowLabel: l, before: b.adjustment, after: a.adjustment });
      }
    }
  }
  return out;
}

// ── Shared table styling ─────────────────────────────────────────────────────
const tableSx = { width: "100%", borderCollapse: "collapse" };
const theadSx = {
  "& th": {
    bgcolor: "#f1f6f9",
    fontSize: 11,
    fontWeight: 700,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    py: 0.85,
    px: 1.25,
    borderBottom: "1px solid #e5e7eb",
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  "& th:first-of-type": { textAlign: "left", position: "sticky", left: 0, zIndex: 1 },
  "& th:last-of-type": { width: 44 },
};
const tbodySx = {
  "& td": { py: 0.4, px: 1.25, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" },
  "& tr:last-of-type td": { borderBottom: "none" },
  "& tr:nth-of-type(even)": { bgcolor: "#fcfcfd" },
  "& tr:hover": { bgcolor: "#f8fafc" },
  "& td:first-of-type": { position: "sticky", left: 0, bgcolor: "inherit", zIndex: 1 },
};

const numCellSx = { width: 96, "& input": { py: 0.4, fontSize: 12.5, fontVariantNumeric: "tabular-nums" } };
const labelCellSx = { width: 170, "& input": { py: 0.4, fontSize: 12.5 } };

function NumberCell({ value, onCommit, step = 0.0001, align = "right", allowNull = false }) {
  return (
    <TextField
      type="number"
      size="small"
      defaultValue={value == null ? "" : value}
      key={String(value)}
      onBlur={(e) => {
        const raw = e.target.value;
        if (allowNull && raw.trim() === "") {
          onCommit(null);
          return;
        }
        const n = Number(raw);
        onCommit(Number.isFinite(n) ? n : value);
      }}
      inputProps={{ step, style: { textAlign: align } }}
      sx={numCellSx}
    />
  );
}

function LabelCell({ value, onCommit }) {
  return (
    <TextField
      size="small"
      defaultValue={value ?? ""}
      key={value}
      onBlur={(e) => {
        const v = e.target.value.trim();
        if (v && v !== value) onCommit(v);
      }}
      sx={labelCellSx}
    />
  );
}

// ── Shape: row × column grid (fico/dscr/ppp) ─────────────────────────────────
function GridShapeTable({ data, onChange, shape, rowLabel }) {
  const allowNull = shape !== SHAPE.PPP_LTV_GRID;
  const rowKeys = Object.keys(data);
  const colKeys = rowKeys.length ? Object.keys(data[rowKeys[0]] || {}) : [];

  const renameRow = (oldRow, newName) => {
    if (!newName || newName === oldRow || data[newName] !== undefined) return;
    const next = {};
    for (const r of rowKeys) next[r === oldRow ? newName : r] = data[r];
    onChange(next);
  };
  const setCell = (row, col, v) => onChange({ ...data, [row]: { ...data[row], [col]: v } });
  const delRow = (row) => {
    const next = { ...data };
    delete next[row];
    onChange(next);
  };

  return (
    <Box component="table" sx={tableSx}>
      <Box component="thead" sx={theadSx}>
        <Box component="tr">
          <Box component="th">{rowLabel}</Box>
          {colKeys.map((c) => (
            <Box component="th" key={c}>
              {c}
            </Box>
          ))}
          <Box component="th" />
        </Box>
      </Box>
      <Box component="tbody" sx={tbodySx}>
        {rowKeys.map((r) => (
          <Box component="tr" key={r}>
            <Box component="td">
              <LabelCell value={r} onCommit={(v) => renameRow(r, v)} />
            </Box>
            {colKeys.map((c) => (
              <Box component="td" key={c} sx={{ textAlign: "right" }}>
                <NumberCell value={data[r]?.[c] ?? null} allowNull={allowNull} onCommit={(v) => setCell(r, c, v)} />
              </Box>
            ))}
            <Box component="td" sx={{ textAlign: "center" }}>
              <IconButton size="small" onClick={() => delRow(r)}>
                <DeleteOutlineRounded sx={{ fontSize: 17, color: "#94a3b8" }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Shape: ltv band map ({ label: adjustment }) ──────────────────────────────
function LtvBandTable({ data, onChange }) {
  const keys = Object.keys(data);
  const renameRow = (oldRow, newName) => {
    if (!newName || newName === oldRow || data[newName] !== undefined) return;
    const next = {};
    for (const k of keys) next[k === oldRow ? newName : k] = data[k];
    onChange(next);
  };
  const setVal = (k, v) => onChange({ ...data, [k]: v });
  const delRow = (k) => {
    const next = { ...data };
    delete next[k];
    onChange(next);
  };
  return (
    <Box component="table" sx={tableSx}>
      <Box component="thead" sx={theadSx}>
        <Box component="tr">
          <Box component="th">Bucket Label</Box>
          <Box component="th">Adjustment</Box>
          <Box component="th" />
        </Box>
      </Box>
      <Box component="tbody" sx={tbodySx}>
        {keys.map((k) => (
          <Box component="tr" key={k}>
            <Box component="td">
              <LabelCell value={k} onCommit={(v) => renameRow(k, v)} />
            </Box>
            <Box component="td" sx={{ textAlign: "right" }}>
              <NumberCell value={data[k]} onCommit={(v) => setVal(k, v)} />
            </Box>
            <Box component="td" sx={{ textAlign: "center" }}>
              <IconButton size="small" onClick={() => delRow(k)}>
                <DeleteOutlineRounded sx={{ fontSize: 17, color: "#94a3b8" }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Shape: loan amount buckets ([{ label, min, max, adjustment }]) ───────────
function BucketsTable({ data, onChange }) {
  const setField = (idx, field, v) => {
    const next = data.slice();
    next[idx] = { ...next[idx], [field]: v };
    onChange(next);
  };
  const delRow = (idx) => onChange(data.filter((_, i) => i !== idx));
  return (
    <Box component="table" sx={tableSx}>
      <Box component="thead" sx={theadSx}>
        <Box component="tr">
          <Box component="th">Label</Box>
          <Box component="th">Min ($)</Box>
          <Box component="th">Max ($)</Box>
          <Box component="th">Adjustment</Box>
          <Box component="th" />
        </Box>
      </Box>
      <Box component="tbody" sx={tbodySx}>
        {data.map((b, i) => (
          <Box component="tr" key={i}>
            <Box component="td">
              <LabelCell value={b.label} onCommit={(v) => setField(i, "label", v)} />
            </Box>
            <Box component="td" sx={{ textAlign: "right" }}>
              <NumberCell value={b.min} step={1000} onCommit={(v) => setField(i, "min", v)} />
            </Box>
            <Box component="td" sx={{ textAlign: "right" }}>
              <NumberCell value={b.max} step={1000} onCommit={(v) => setField(i, "max", v)} />
            </Box>
            <Box component="td" sx={{ textAlign: "right" }}>
              <NumberCell value={b.adjustment} onCommit={(v) => setField(i, "adjustment", v)} />
            </Box>
            <Box component="td" sx={{ textAlign: "center" }}>
              <IconButton size="small" onClick={() => delRow(i)}>
                <DeleteOutlineRounded sx={{ fontSize: 17, color: "#94a3b8" }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Bulk shift bar ───────────────────────────────────────────────────────────
function BulkShiftBar({ busy, onApply }) {
  const [val, setVal] = useState("");
  const [sign, setSign] = useState(1);
  const apply = () => {
    const n = Number(val);
    if (!Number.isFinite(n) || n === 0) return;
    onApply(n * sign);
  };
  return (
    <Flx ac g={1}>
      <TextField
        size="small"
        label="Bulk shift (price pts)"
        type="number"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={{ step: 0.01 }}
        sx={{ width: 180 }}
      />
      <Button size="small" variant={sign === -1 ? "contained" : "outlined"} onClick={() => setSign(-1)} sx={{ minWidth: 36 }}>
        −
      </Button>
      <Button size="small" variant={sign === 1 ? "contained" : "outlined"} onClick={() => setSign(1)} sx={{ minWidth: 36 }}>
        +
      </Button>
      <Button size="small" variant="contained" disableElevation onClick={apply} disabled={busy} sx={{ textTransform: "none" }}>
        Apply
      </Button>
    </Flx>
  );
}

// ── Diff (review) modal ────────────────────────────────────────────────────
function DiffModal({ open, shape, before, after, onConfirm, onClose, saving }) {
  const diff = useMemo(() => (open ? computeDiff(shape, before, after) : []), [open, shape, before, after]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
        Review changes ({diff.length} {diff.length === 1 ? "cell" : "cells"} different)
      </DialogTitle>
      <DialogContent dividers>
        {diff.length === 0 ? (
          <Txt sx={{ fontSize: 13, color: "#94a3b8" }}>No differences.</Txt>
        ) : (
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <Box component="thead" sx={{ "& th": { p: "6px 8px", color: "#64748b", fontWeight: 700, fontSize: 11 } }}>
              <tr>
                <th style={{ textAlign: "left" }}>Row</th>
                {isGridShape(shape) && <th style={{ textAlign: "left" }}>Column</th>}
                <th style={{ textAlign: "right" }}>Before</th>
                <th style={{ textAlign: "right" }}>After</th>
              </tr>
            </Box>
            <tbody>
              {diff.map((d, i) => (
                <tr key={i}>
                  <td style={{ padding: "6px 8px" }}>{d.rowLabel}</td>
                  {isGridShape(shape) && <td style={{ padding: "6px 8px" }}>{d.colLabel}</td>}
                  <td style={{ textAlign: "right", padding: "6px 8px", fontVariantNumeric: "tabular-nums" }}>
                    <s style={{ color: "#dc2626" }}>{d.before === null ? "—" : fmtNum(d.before)}</s>
                  </td>
                  <td style={{ textAlign: "right", padding: "6px 8px", fontVariantNumeric: "tabular-nums" }}>
                    <b>{d.after === null ? "—" : fmtNum(d.after)}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={onConfirm} disabled={saving || diff.length === 0} sx={{ textTransform: "none" }}>
          {saving ? "Saving…" : "Confirm & save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Lock dialog ──────────────────────────────────────────────────────────────
function LockDialog({ open, onClose, onSubmit, saving, version }) {
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!open) setNote("");
  }, [open]);
  const trimmed = note.trim();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Lock version v{version}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          required
          label="Note"
          placeholder="Why are you locking this version?"
          multiline
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={() => onSubmit(trimmed)} disabled={!trimmed || saving} sx={{ textTransform: "none" }}>
          {saving ? "Locking…" : "Lock"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdjustmentTableEditor({ tableKey, shape, rowLabel = "Row" }) {
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = () => {
    setLoading(true);
    return adminPricingApi
      .getAdjustmentTable(tableKey)
      .then((d) => {
        setDoc(d);
        setDraft(deepClone(d.data));
      })
      .catch(() => {
        setDoc(null);
        setDraft(null);
      })
      .finally(() => setLoading(false));
  };

  // Load once on mount (component is mounted lazily when its accordion opens).
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);

  const dirty = useMemo(() => {
    if (!doc || draft === null) return false;
    return !dataEqual(doc.data, draft);
  }, [doc, draft]);

  const lockBlocked = doc ? doc.version === doc.locked_version : true;

  const contiguityWarning = useMemo(() => {
    if (shape !== SHAPE.LOAN_AMOUNT_BUCKETS || !Array.isArray(draft) || draft.length <= 1) return null;
    const sorted = draft.slice().sort((a, b) => a.min - b.min);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].min !== sorted[i - 1].max) {
        return `Gap/overlap between "${sorted[i - 1].label}" and "${sorted[i].label}"`;
      }
    }
    return null;
  }, [shape, draft]);

  const addRow = () => {
    if (isGridShape(shape)) {
      const rowKeys = Object.keys(draft);
      const colKeys = rowKeys.length ? Object.keys(draft[rowKeys[0]] || {}) : [];
      const newRow = {};
      for (const c of colKeys) newRow[c] = 0;
      let name = "new_row";
      let i = 1;
      while (draft[name] !== undefined) name = `new_row_${i++}`;
      setDraft({ ...draft, [name]: newRow });
    } else if (shape === SHAPE.LTV_BAND_MAP) {
      let name = "new_band";
      let i = 1;
      while (draft[name] !== undefined) name = `new_band_${i++}`;
      setDraft({ ...draft, [name]: 0 });
    } else {
      const lastMax = draft.length ? draft[draft.length - 1].max : 0;
      setDraft([...draft, { label: "New bucket", min: lastMax, max: lastMax + 100000, adjustment: 0 }]);
    }
  };

  const addColumn = () => {
    if (!isGridShape(shape)) return;
    let name = "new_col";
    let i = 1;
    const first = Object.values(draft)[0];
    while (first && first[name] !== undefined) name = `new_col_${i++}`;
    const next = {};
    for (const r of Object.keys(draft)) next[r] = { ...draft[r], [name]: 0 };
    setDraft(next);
  };

  const onConfirmSave = () => {
    setSaving(true);
    adminPricingApi
      .saveAdjustmentTable(tableKey, draft)
      .then(() => {
        setDiffOpen(false);
        return load();
      })
      .catch((e) => window.alert(`Save failed: ${e?.response?.data?.detail || e?.message || e}`))
      .finally(() => setSaving(false));
  };

  const onBulkShift = (delta) => {
    setBusy(true);
    adminPricingApi
      .bulkShiftAdjustmentTable(tableKey, delta)
      .then(() => load())
      .catch((e) => window.alert(`Shift failed: ${e?.response?.data?.detail || e?.message || e}`))
      .finally(() => setBusy(false));
  };

  const onLock = (note) => {
    setBusy(true);
    adminPricingApi
      .lockAdjustmentTable(tableKey, note)
      .then(() => {
        setLockOpen(false);
        return load();
      })
      .catch((e) => window.alert(`Lock failed: ${e?.response?.data?.detail || e?.message || e}`))
      .finally(() => setBusy(false));
  };

  if (loading || !doc || draft === null) {
    return (
      <Flx center sx={{ py: 4 }}>
        <CircularProgress size={20} sx={{ color: teal[700] }} />
      </Flx>
    );
  }

  return (
    <Flx column g={2}>
      <Flx ac jb g={1} sx={{ flexWrap: "wrap" }}>
        <BulkShiftBar busy={busy} onApply={onBulkShift} />
        <Flx ac g={1} sx={{ flexWrap: "wrap" }}>
          <Txt sx={{ fontSize: 11.5, color: "#64748b" }}>
            v{doc.version} · locked v{doc.locked_version}
          </Txt>
          <Tooltip title={lockBlocked ? "Save changes first" : "Pin the current version as the live one"}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<LockOutlined sx={{ fontSize: 16 }} />}
                onClick={() => setLockOpen(true)}
                disabled={lockBlocked || dirty || busy}
                sx={{ textTransform: "none" }}
              >
                Lock
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<HistoryRounded sx={{ fontSize: 16 }} />}
            onClick={() => setHistoryOpen(true)}
            sx={{ textTransform: "none" }}
          >
            History
          </Button>
        </Flx>
      </Flx>

      {contiguityWarning && (
        <Alert severity="warning" sx={{ py: 0, "& .MuiAlert-message": { fontSize: 12.5 } }}>
          {contiguityWarning}
        </Alert>
      )}

      <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflow: "auto", maxWidth: "100%" }}>
        {isGridShape(shape) ? (
          <GridShapeTable data={draft} onChange={setDraft} shape={shape} rowLabel={rowLabel} />
        ) : shape === SHAPE.LTV_BAND_MAP ? (
          <LtvBandTable data={draft} onChange={setDraft} />
        ) : (
          <BucketsTable data={draft} onChange={setDraft} />
        )}
      </Box>

      <Flx ac g={1.5} sx={{ flexWrap: "wrap" }}>
        <Button size="small" variant="text" startIcon={<AddRounded sx={{ fontSize: 16 }} />} onClick={addRow} sx={{ textTransform: "none" }}>
          Add row
        </Button>
        {isGridShape(shape) && (
          <Button size="small" variant="text" startIcon={<AddRounded sx={{ fontSize: 16 }} />} onClick={addColumn} sx={{ textTransform: "none" }}>
            Add column
          </Button>
        )}
        {dirty && <Chip label="Unsaved changes" color="warning" size="small" sx={{ height: 20, fontSize: 11 }} />}
        <Box sx={{ flex: 1 }} />
        {dirty && (
          <>
            <Button variant="text" onClick={() => setDraft(deepClone(doc.data))} sx={{ textTransform: "none" }}>
              Discard
            </Button>
            <Button variant="contained" disableElevation onClick={() => setDiffOpen(true)} disabled={saving} sx={{ textTransform: "none", fontWeight: 600 }}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </>
        )}
      </Flx>

      <DiffModal open={diffOpen} shape={shape} before={doc.data} after={draft} onConfirm={onConfirmSave} onClose={() => setDiffOpen(false)} saving={saving} />
      <LockDialog open={lockOpen} onClose={() => setLockOpen(false)} onSubmit={onLock} saving={busy} version={doc.version} />
      <ChangeHistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} entityType="adjustment_table" entityId={tableKey} />
    </Flx>
  );
}
